
import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";

import {
    ArrowRight,
    BedDouble,
    CheckCircle2,
    ChevronRight,
    Compass,
    Hotel,
    MapPin,
    ShieldCheck,
    Sparkles,
    Star,
    Search,
    LoaderCircle,
    CircleAlert,
} from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Hero from "../components/Hero";
import heroImg from "../assests/images/hero.jpg";

const API_URL = "https://dubaibudgetivehotels.com/api";
const HOTEL_IMAGE_BASE =
    "https://uaediscountedhotels.com/uploads/hotels/";

const getHotelImage = (image) => {
    if (!image) return heroImg;

    const value = String(image).trim();

    if (/^https?:\/\//i.test(value)) return value;

    if (value.startsWith("/uploads/")) {
        return `https://uaediscountedhotels.com${value}`;
    }

    if (value.startsWith("uploads/")) {
        return `https://uaediscountedhotels.com/${value}`;
    }

    return `${HOTEL_IMAGE_BASE}${value.replace(/^\/+/, "")}`;
};

const stripHtml = (html) => {
    if (!html) return "";

    return String(html)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/\s+/g, " ")
        .trim();
};

const formatPrice = (value) => {
    const amount = Number(value);

    if (!Number.isFinite(amount) || amount <= 0) return null;

    return amount.toLocaleString("en-AE", {
        maximumFractionDigits: 0,
    });
};

function HotelImage({ image, name }) {
    const [failed, setFailed] = useState(false);
    const src = failed ? heroImg : getHotelImage(image);

    useEffect(() => {
        setFailed(false);
    }, [image]);

    return (
        <img
            src={src}
            alt={name || "Dubai hotel"}
            loading="lazy"
            onError={() => setFailed(true)}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
    );
}

export default function DestinationDetails() {
    const { slug } = useParams();

    const [destination, setDestination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let active = true;

        const fetchDestination = async () => {
            setLoading(true);
            setError("");
            setDestination(null);

            try {
                const res = await axios.get(
                    `${API_URL}/destinations/${encodeURIComponent(slug)}`
                );

                if (!active) return;

                setDestination(res.data);
            } catch (err) {
                if (!active) return;

                console.error("Failed to fetch destination:", err);
                setError("We couldn't load this destination.");
                setDestination(null);
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchDestination();

        return () => {
            active = false;
        };
    }, [slug]);

    const hotels = Array.isArray(destination?.hotels)
        ? destination.hotels
        : [];

    // LOADING
    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5FAF7]">
                <Header />

                <div className="mx-auto flex min-h-[65vh] max-w-6xl flex-col items-center justify-center px-6 text-center">
                    <LoaderCircle
                        size={44}
                        className="animate-spin text-[#168B43]"
                    />

                    <h2 className="mt-6 text-[22px] font-extrabold text-[#17324D]">
                        Discovering Your Destination
                    </h2>

                    <p className="mt-3 text-[13px] text-[#718096]">
                        Finding hotels and places for your next stay.
                    </p>
                </div>

                <Footer />
            </div>
        );
    }

    // NOT FOUND
    if (!destination) {
        return (
            <div className="min-h-screen bg-[#F5FAF7]">
                <Header />

                <div className="mx-auto flex min-h-[65vh] max-w-xl flex-col items-center justify-center px-5 text-center">
                    <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-[#E8F7EE] text-[#168B43]">
                        <CircleAlert size={34} />
                    </div>

                    <p className="mt-7 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#168B43]">
                        Explore Dubai
                    </p>

                    <h1 className="mt-3 text-[36px] font-extrabold tracking-tight text-[#17324D]">
                        Destination Not Found
                    </h1>

                    <p className="mt-4 text-[14px] leading-8 text-[#718096]">
                        {error ||
                            "This destination is not available. Browse our other destinations to find your ideal stay."}
                    </p>

                    <Link
                        to="/destinations"
                        className="mt-8 inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-[13px] font-extrabold text-white transition hover:bg-[#087D37]"
                    >
                        Browse Destinations
                        <ArrowRight size={18} />
                    </Link>
                </div>

                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5FAF7] text-[#17324D]">
            {/* HERO */}
            <section className="relative">
                <Hero
                    variant="inner"
                    heightClass="h-[320px] sm:h-[370px] lg:h-[420px]"
                    title={destination.name}
                    subtitle={`Explore beautiful hotels and memorable stays in ${destination.name}.`}
                    backgroundImage={heroImg}
                />

                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#032F28]/75 via-[#064E3B]/10 to-transparent" />

                <div className="absolute bottom-7 left-0 right-0 z-20">
                    <nav
                        aria-label="Breadcrumb"
                        className="mx-auto flex max-w-[1536px] flex-wrap items-center gap-2 px-4 text-[12px] text-white/80 sm:px-6 lg:px-8"
                    >
                        <Link to="/" className="hover:text-[#A5E8B8]">
                            Home
                        </Link>

                        <ChevronRight size={13} />

                        <Link
                            to="/destinations"
                            className="hover:text-[#A5E8B8]"
                        >
                            Destinations
                        </Link>

                        <ChevronRight size={13} />

                        <span className="font-extrabold text-[#A5E8B8]">
                            {destination.name}
                        </span>
                    </nav>
                </div>
            </section>

            <main>
                {/* DESTINATION INTRO */}
                <section className="relative overflow-hidden px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
                    <div className="pointer-events-none absolute -right-20 top-0 h-80 w-80 rounded-full bg-[#DDF5E6] opacity-60 blur-3xl" />

                    <div className="relative mx-auto grid max-w-[1536px] items-center gap-10 lg:grid-cols-[minmax(0,1fr)_330px] lg:gap-16">
                        <div>
                            <div className="inline-flex items-center gap-2 rounded-full border border-[#CBEAD6] bg-white px-5 py-2.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#168B43]">
                                <Compass size={16} />
                                Discover Your Destination
                            </div>

                            <h1 className="mt-7 max-w-[780px] text-[36px] font-extrabold leading-[1.15] tracking-tight text-[#17324D] sm:text-[47px] lg:text-[55px]">
                                Find Your Perfect Stay in{" "}
                                <span className="text-[#168B43]">
                                    {destination.name}
                                </span>
                            </h1>

                            <p className="mt-6 max-w-[700px] text-[14px] leading-8 text-[#718096]">
                                Explore hotels in {destination.name}, compare
                                accommodation options, and choose the stay that
                                suits your Dubai visit.
                            </p>

                            <div className="mt-8 flex flex-wrap gap-x-7 gap-y-4">
                                <span className="inline-flex items-center gap-2 text-[12px] font-bold text-[#40556A]">
                                    <CheckCircle2 size={17} className="text-[#168B43]" />
                                    Explore Hotel Options
                                </span>

                                <span className="inline-flex items-center gap-2 text-[12px] font-bold text-[#40556A]">
                                    <ShieldCheck size={17} className="text-[#168B43]" />
                                    Secure Booking
                                </span>

                                <span className="inline-flex items-center gap-2 text-[12px] font-bold text-[#40556A]">
                                    <MapPin size={17} className="text-[#168B43]" />
                                    Dubai, UAE
                                </span>
                            </div>
                        </div>

                        {/* HOTEL COUNT CARD */}
                        <div className="relative overflow-hidden rounded-[26px] border border-[#DCEAE1] bg-white p-8 shadow-[0_15px_45px_rgba(6,78,59,0.06)] sm:p-10">
                            <div className="absolute left-0 top-0 h-1.5 w-full bg-[#168B43]" />

                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#168B43]">
                                <Hotel size={28} />
                            </div>

                            <div className="mt-7 text-[70px] font-extrabold leading-none tracking-tight text-[#17324D]">
                                {hotels.length}
                            </div>

                            <p className="mt-3 text-[12px] font-extrabold uppercase tracking-[0.16em] text-[#168B43]">
                                Hotels To Explore
                            </p>

                            <p className="mt-5 border-t border-[#E8F0EA] pt-5 text-[13px] leading-7 text-[#718096]">
                                Browse accommodation currently listed in{" "}
                                {destination.name}.
                            </p>
                        </div>
                    </div>
                </section>

                {/* HOTEL LIST */}
                <section className="border-t border-[#E8F0EA] bg-white px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
                    <div className="mx-auto max-w-[1536px]">
                        <div className="mb-9 flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
                            <div>
                                <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#168B43]">
                                    <Sparkles size={16} />
                                    Stay In {destination.name}
                                </p>

                                <h2 className="mt-3 text-[32px] font-extrabold tracking-tight text-[#17324D] sm:text-[42px]">
                                    Explore the{" "}
                                    <span className="text-[#168B43]">
                                        Hotels
                                    </span>
                                </h2>

                                <p className="mt-3 text-[13px] text-[#718096]">
                                    Find accommodation that fits your travel plans.
                                </p>
                            </div>

                            <div className="inline-flex items-center gap-2 rounded-full border border-[#DCEAE1] bg-[#F5FAF7] px-5 py-3 text-[12px] font-bold text-[#47715A]">
                                <Hotel size={17} className="text-[#168B43]" />
                                {hotels.length}{" "}
                                {hotels.length === 1 ? "Property" : "Properties"} Listed
                            </div>
                        </div>

                        {hotels.length > 0 ? (
                            <div className="space-y-6">
                                {hotels.map((hotel, index) => {
                                    const description =
                                        stripHtml(hotel.short_description) ||
                                        "Discover a comfortable stay with convenient access to Dubai's attractions.";

                                    const price = formatPrice(hotel.price_per_night);
                                    const hotelUrl = `/hotels/${hotel.slug}`;

                                    return (
                                        <article
                                            key={hotel.id || hotel.slug || index}
                                            className="group grid overflow-hidden rounded-[24px] border border-[#DCEAE1] bg-white shadow-[0_8px_30px_rgba(6,78,59,0.035)] transition-all duration-300 hover:-translate-y-1 hover:border-[#B9DEC9] hover:shadow-[0_20px_50px_rgba(6,78,59,0.09)] md:grid-cols-[285px_minmax(0,1fr)] lg:grid-cols-[350px_minmax(0,1fr)_230px]"
                                        >
                                            {/* HOTEL IMAGE */}
                                            <Link
                                                to={hotelUrl}
                                                className="relative block h-[245px] overflow-hidden md:h-full md:min-h-[250px]"
                                            >
                                                <HotelImage
                                                    image={hotel.image}
                                                    name={hotel.name}
                                                />

                                                <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-[#064E3B]/95 px-4 py-2 text-[11px] font-extrabold text-white shadow-md">
                                                    <Hotel size={14} className="text-[#A5E8B8]" />
                                                    Hotel {String(index + 1).padStart(2, "0")}
                                                </div>
                                            </Link>

                                            {/* HOTEL DETAILS */}
                                            <div className="flex flex-col justify-center p-6 sm:p-8">
                                                <div className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#168B43]">
                                                    <MapPin size={15} />
                                                    {destination.name}
                                                </div>

                                                <Link
                                                    to={hotelUrl}
                                                    className="mt-4 text-[24px] font-extrabold leading-tight tracking-tight text-[#17324D] transition hover:text-[#168B43] sm:text-[29px]"
                                                >
                                                    {hotel.name}
                                                </Link>

                                                {Number(hotel.rating) > 0 && (
                                                    <div className="mt-3 flex items-center gap-1">
                                                        {Array.from({ length: Math.min(5, Math.floor(Number(hotel.rating))) }).map((_, i) => (
                                                            <Star
                                                                key={i}
                                                                size={15}
                                                                fill="#F4B740"
                                                                stroke="#F4B740"
                                                            />
                                                        ))}

                                                        <span className="ml-2 text-[12px] font-semibold text-[#718096]">
                                                            {hotel.rating} Star Hotel
                                                        </span>
                                                    </div>
                                                )}

                                                <p className="mt-4 line-clamp-3 max-w-[620px] text-[13px] leading-7 text-[#718096]">
                                                    {description}
                                                </p>

                                                <Link
                                                    to={hotelUrl}
                                                    className="mt-6 inline-flex w-fit items-center gap-2 text-[13px] font-extrabold text-[#168B43] transition hover:text-[#064E3B] lg:hidden"
                                                >
                                                    View Hotel Details
                                                    <ArrowRight size={17} />
                                                </Link>
                                            </div>

                                            {/* DESKTOP PRICE */}
                                            <div className="hidden flex-col justify-center border-l border-[#E8F0EA] bg-[#F8FBF9] px-7 py-8 lg:flex">
                                                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#94A3B8]">
                                                    Starting From
                                                </p>

                                                {price ? (
                                                    <>
                                                        <p className="mt-3 text-[34px] font-extrabold tracking-tight text-[#17324D]">
                                                            <span className="mr-1 text-[15px] font-bold text-[#168B43]">
                                                                AED
                                                            </span>
                                                            {price}
                                                        </p>

                                                        <p className="mt-1 text-[12px] text-[#718096]">
                                                            Per Night
                                                        </p>
                                                    </>
                                                ) : (
                                                    <p className="mt-3 text-[15px] font-extrabold text-[#17324D]">
                                                        Check Rates
                                                    </p>
                                                )}

                                                <Link
                                                    to={hotelUrl}
                                                    className="mt-7 flex items-center justify-center gap-2 rounded-xl bg-[#168B43] px-4 py-4 text-[12px] font-extrabold text-white transition hover:bg-[#087D37]"
                                                >
                                                    View Hotel
                                                    <ArrowRight size={16} />
                                                </Link>
                                            </div>

                                            {/* MOBILE PRICE */}
                                            <div className="flex items-center justify-between gap-4 border-t border-[#E8F0EA] bg-[#F8FBF9] px-6 py-4 lg:hidden">
                                                <div>
                                                    <p className="text-[11px] text-[#94A3B8]">
                                                        Starting From
                                                    </p>

                                                    <p className="mt-1 text-[14px] font-extrabold text-[#17324D]">
                                                        {price ? `AED ${price} / night` : "Check Rates"}
                                                    </p>
                                                </div>

                                                <Link
                                                    to={hotelUrl}
                                                    aria-label={`View ${hotel.name}`}
                                                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#168B43] text-white transition hover:bg-[#087D37]"
                                                >
                                                    <ArrowRight size={19} />
                                                </Link>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="rounded-[24px] border border-dashed border-[#B9DEC9] bg-[#F5FAF7] px-6 py-16 text-center">
                                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#168B43]">
                                    <Hotel size={30} />
                                </div>

                                <h3 className="mt-6 text-[27px] font-extrabold text-[#17324D]">
                                    No Hotels Available Yet
                                </h3>

                                <p className="mx-auto mt-4 max-w-[470px] text-[13px] leading-8 text-[#718096]">
                                    There are currently no hotels listed in{" "}
                                    {destination.name}. You can explore other
                                    destinations instead.
                                </p>

                                <Link
                                    to="/destinations"
                                    className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#168B43] px-6 py-4 text-[13px] font-extrabold text-white hover:bg-[#087D37]"
                                >
                                    Explore Destinations
                                    <ArrowRight size={17} />
                                </Link>
                            </div>
                        )}
                    </div>
                </section>

                {/* BOTTOM CTA */}
                <section className="relative overflow-hidden bg-[#064E3B] px-4 py-16 text-white sm:px-6 lg:px-8 lg:py-20">
                    <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#168B43]/25 blur-3xl" />

                    <div className="relative mx-auto flex max-w-[1536px] flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
                        <div>
                            <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#A5E8B8]">
                                <Compass size={16} />
                                Discover More
                            </p>

                            <h2 className="mt-4 text-[32px] font-extrabold tracking-tight text-white sm:text-[42px]">
                                Discover More of{" "}
                                <span className="text-[#A5E8B8]">
                                    Dubai.
                                </span>
                            </h2>

                            <p className="mt-4 max-w-[650px] text-[14px] leading-8 text-white/70">
                                Explore more hotels and destinations to find the
                                right stay for your next Dubai trip.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            <Link
                                to="/hotels"
                                className="inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-6 py-4 text-[12px] font-extrabold text-white transition hover:bg-[#21A253]"
                            >
                                Explore All Hotels
                                <ArrowRight size={17} />
                            </Link>

                            <Link
                                to="/destinations"
                                className="inline-flex items-center gap-3 rounded-xl border border-white/30 px-6 py-4 text-[12px] font-extrabold text-white transition hover:bg-white/10"
                            >
                                More Destinations
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
